import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const planColors = {
  demo: 'border-gray-300 bg-gray-50',
  basico: 'border-green-300 bg-green-50',
  profesional: 'border-blue-300 bg-blue-50',
  enterprise: 'border-purple-300 bg-purple-50',
  corporativo: 'border-amber-400 bg-amber-50',
};

const planBadgeColors = {
  demo: 'bg-gray-100 text-gray-700',
  basico: 'bg-green-100 text-green-800',
  profesional: 'bg-blue-100 text-blue-800',
  enterprise: 'bg-purple-100 text-purple-800',
  corporativo: 'bg-amber-100 text-amber-800',
};

const formatLimit = (val) => {
  if (val === -1) return 'Ilimitado';
  return val.toLocaleString();
};

const AdminLicenciasPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [form, setForm] = useState({ nombre: '', email: '', telefono: '', direccion: '', plan: 'trial', duracion_meses: 12 });

  useEffect(() => {
    if (user?.rol !== 'superadmin') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [clRes, plRes] = await Promise.allSettled([adminAPI.getClientes(), adminAPI.getPlanes()]);
      if (clRes.status === 'fulfilled') setClientes(clRes.value.data.clientes);
      if (plRes.status === 'fulfilled') setPlanes(plRes.value.data.planes);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCliente) {
        await adminAPI.updateCliente(editingCliente.id, form);
      } else {
        await adminAPI.createCliente(form);
      }
      setShowForm(false);
      setEditingCliente(null);
      setForm({ nombre: '', email: '', telefono: '', direccion: '', plan: 'trial', duracion_meses: 12 });
      loadData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleToggle = async (cliente) => {
    try {
      if (cliente.activo) {
        await adminAPI.suspenderCliente(cliente.id);
      } else {
        await adminAPI.reactivarCliente(cliente.id);
      }
      loadData();
    } catch { /* silent */ }
  };

  const openEdit = (cl) => {
    setEditingCliente(cl);
    setForm({ nombre: cl.nombre, email: cl.email, telefono: cl.telefono || '', direccion: cl.direccion || '', plan: cl.plan || 'trial', renovar_meses: 0, limite_animales: cl.limite_animales || null });
    setShowForm(true);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="inline-block w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="admin-licencias-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Administracion de Licencias</h2>
          <p className="text-sm text-gray-500 mt-1">{clientes.length} clientes registrados</p>
        </div>
        <button
          onClick={() => { setEditingCliente(null); setForm({ nombre: '', email: '', telefono: '', direccion: '', plan: 'trial', duracion_meses: 12 }); setShowForm(true); }}
          className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium text-sm"
          data-testid="new-client-btn"
        >
          + Nuevo Cliente
        </button>
      </div>

      {/* Plans overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {planes.map((p) => (
          <div
            key={p.id}
            className={`rounded-xl border-2 p-5 transition hover:shadow-md ${planColors[p.id] || 'border-gray-200 bg-gray-50'} ${p.id === 'corporativo' ? 'ring-2 ring-amber-400/50' : ''}`}
            data-testid={`plan-card-${p.id}`}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-gray-900">{p.nombre}</h3>
              {p.id === 'trial' && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full font-medium">Gratis 30 dias</span>}
              {p.id === 'corporativo' && <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-medium">Premium</span>}
            </div>
            <p className="text-2xl font-bold text-green-700">
              {p.id === 'corporativo' ? 'A medida' : p.precio_mensual === 0 ? 'Gratis' : `$${p.precio_mensual}`}
              {p.precio_mensual > 0 && p.id !== 'corporativo' && <span className="text-sm text-gray-500 font-normal">/mes</span>}
            </p>
            <p className="text-xs text-gray-500 mt-1 mb-3">{p.id === 'corporativo' ? 'Consultar presupuesto' : p.descripcion}</p>
            <div className="space-y-1 text-sm text-gray-600">
              <p>Animales: <strong>{p.limite_animales_display}</strong></p>
              <p>Usuarios: <strong>{p.limite_usuarios_display}</strong></p>
              <p>Establecimientos: <strong>{p.limite_establecimientos_display}</strong></p>
              <p className="text-green-700 font-medium">IA Asistente: Incluido</p>
              <p className="text-green-700 font-medium">Alertas automaticas IA: Incluido</p>
            </div>
          </div>
        ))}
      </div>

      {/* Clients table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="clients-table">
            <thead className="bg-gray-50">
              <tr>
                {['Cliente', 'Plan', 'Estado', 'Animales', 'Usuarios', 'Establec.', 'Expiracion', 'Acciones'].map(h => (
                  <th key={h} className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clientes.map((cl) => (
                <tr key={cl.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <p className="font-semibold text-gray-900">{cl.nombre}</p>
                    <p className="text-xs text-gray-500">{cl.email}</p>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${planBadgeColors[cl.plan] || 'bg-gray-100 text-gray-700'}`}>
                      {(cl.plan || 'trial').toUpperCase()}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${cl.estado_licencia === 'activa' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {cl.estado_licencia}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {cl.uso?.animales || 0} / {formatLimit(cl.limite_animales)}
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {cl.uso?.usuarios || 0} / {formatLimit(cl.limite_usuarios)}
                  </td>
                  <td className="py-3 px-4 text-gray-600">
                    {cl.uso?.establecimientos || 0} / {formatLimit(cl.limite_establecimientos)}
                  </td>
                  <td className="py-3 px-4 text-xs text-gray-600">{cl.fecha_expiracion ? new Date(cl.fecha_expiracion).toLocaleDateString('es-AR') : '--'}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <button onClick={() => openEdit(cl)} className="px-3 py-1 border border-gray-300 rounded text-xs hover:bg-gray-100" data-testid={`edit-client-${cl.id}`}>Editar</button>
                      <button
                        onClick={() => handleToggle(cl)}
                        className={`px-3 py-1 rounded text-xs font-semibold ${cl.activo ? 'bg-red-50 text-red-700 hover:bg-red-100' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
                        data-testid={`toggle-client-${cl.id}`}
                      >
                        {cl.activo ? 'Suspender' : 'Reactivar'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" data-testid="client-form-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{editingCliente ? 'Editar Cliente' : 'Nuevo Cliente'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <input type="text" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} required className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="client-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="client-email-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                  <input type="text" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                  <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="client-plan-select">
                    {planes.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.nombre} {p.id === 'corporativo' ? '(Consultar presupuesto)' : p.precio_mensual === 0 ? '(Gratis)' : `($${p.precio_mensual}/mes)`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Direccion</label>
                <input type="text" value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              {!editingCliente && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duracion (meses)</label>
                  <select value={form.duracion_meses} onChange={(e) => setForm({ ...form, duracion_meses: parseInt(e.target.value) })} className="w-full border rounded-lg px-3 py-2 text-sm">
                    <option value={1}>1 mes</option>
                    <option value={3}>3 meses</option>
                    <option value={6}>6 meses</option>
                    <option value={12}>12 meses</option>
                    <option value={24}>24 meses</option>
                  </select>
                </div>
              )}
              {editingCliente && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Renovar licencia (meses extra)</label>
                  <select value={form.renovar_meses || 0} onChange={(e) => setForm({ ...form, renovar_meses: parseInt(e.target.value) })} className="w-full border rounded-lg px-3 py-2 text-sm">
                    <option value={0}>No renovar</option>
                    <option value={1}>+1 mes</option>
                    <option value={3}>+3 meses</option>
                    <option value={6}>+6 meses</option>
                    <option value={12}>+12 meses</option>
                  </select>
                </div>
              )}
              {editingCliente && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <label className="block text-xs font-semibold text-amber-800 mb-2">Limite custom de animales (deja vacio para usar el del plan)</label>
                  <input type="number" min="0" placeholder="Ej: 15000"
                    value={form.limite_animales || ''} onChange={(e) => setForm({ ...form, limite_animales: e.target.value ? parseInt(e.target.value) : null })}
                    className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm" data-testid="custom-animal-limit" />
                </div>
              )}
              {/* Selected plan summary */}
              {(() => {
                const selectedPlan = planes.find(p => p.id === form.plan);
                if (!selectedPlan) return null;
                return (
                  <div className={`rounded-lg border p-3 text-sm ${planColors[selectedPlan.id] || 'bg-gray-50'}`} data-testid="selected-plan-summary">
                    <p className="font-semibold text-gray-800 mb-1">Plan {selectedPlan.nombre}</p>
                    <div className="grid grid-cols-3 gap-2 text-xs text-gray-600">
                      <span>Animales: {selectedPlan.limite_animales_display}</span>
                      <span>Usuarios: {selectedPlan.limite_usuarios_display}</span>
                      <span>Establec: {selectedPlan.limite_establecimientos_display}</span>
                    </div>
                  </div>
                );
              })()}
              <div className="flex justify-end space-x-3 pt-2">
                <button type="button" onClick={() => { setShowForm(false); setEditingCliente(null); }} className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm" data-testid="cancel-form-btn">Cancelar</button>
                <button type="submit" className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium" data-testid="submit-client-btn">{editingCliente ? 'Guardar Cambios' : 'Crear Cliente'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLicenciasPage;
